(() => {
  'use strict';
  if (!window.customElements || customElements.get('fd-on-request-delivery')) return;
  const sofiaClock = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Sofia', year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23'
  });
  const localParts = instant => {
    const parts = {};
    for (const part of sofiaClock.formatToParts(instant)) {
      if (part.type !== 'literal') parts[part.type] = Number(part.value);
    }
    return parts;
  };
  const zonedCutoff = (day, hour) => {
    const wallTime = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), hour);
    let instant = wallTime;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const parts = localParts(new Date(instant));
      const observed = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
      const correction = wallTime - observed;
      instant += correction;
      if (correction === 0) break;
    }
    return instant;
  };
  class FdOnRequestDelivery extends HTMLElement {
    constructor() {
      super();
      this.refresh = this.refresh.bind(this);
      this.resume = () => {
        if (document.hidden) window.clearTimeout(this.timer);
        else this.refresh();
      };
    }
    connectedCallback() {
      this.refresh();
      document.addEventListener('visibilitychange', this.resume);
      window.addEventListener('pageshow', this.refresh);
    }
    disconnectedCallback() {
      window.clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.resume);
      window.removeEventListener('pageshow', this.refresh);
    }
    refresh() {
      window.clearTimeout(this.timer);
      if (!this.isConnected) return;
      this.updateDates();
      if (!document.hidden) this.timer = window.setTimeout(this.refresh, 1000 - (Date.now() % 1000) + 10);
    }
    updateDates() {
      try {
        const now = Date.now();
        const parts = localParts(new Date(now));
        const today = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
        const weekday = today.getUTCDay();
        const seconds = parts.hour * 3600 + parts.minute * 60 + parts.second;
        const cutoffDay = new Date(today);
        let cutoffHour = 9;
        let deliveryOffset = 4;
        if (this.dataset.deliveryMode === 'express') {
          let dispatchOffset = 0;
          if (weekday === 0) dispatchOffset = 1;
          else if (weekday === 6) dispatchOffset = seconds > 32400 ? 2 : 0;
          else if (seconds > 50400) dispatchOffset = 1;
          cutoffDay.setUTCDate(cutoffDay.getUTCDate() + dispatchOffset);
          const saturdayDispatch = cutoffDay.getUTCDay() === 6;
          cutoffHour = saturdayDispatch ? 9 : 14;
          deliveryOffset = saturdayDispatch ? 2 : 1;
        } else {
          const daysToMonday = (8 - weekday) % 7;
          const daysToThursday = (11 - weekday) % 7;
          const mondayWait = daysToMonday === 0 && seconds > 32400 ? 7 : daysToMonday;
          const thursdayWait = daysToThursday === 0 && seconds > 32400 ? 7 : daysToThursday;
          const useMonday = mondayWait <= thursdayWait;
          cutoffDay.setUTCDate(cutoffDay.getUTCDate() + (useMonday ? mondayWait : thursdayWait));
          deliveryOffset = useMonday ? 4 : 6;
        }
        const delivery = new Date(cutoffDay);
        delivery.setUTCDate(delivery.getUTCDate() + deliveryOffset);
        const iso = delivery.toISOString().slice(0, 10);
        const target = this.querySelector('[data-fd-delivery-date]');
        if (target && target.dataset.date !== iso) {
          const locale = (this.dataset.locale || 'bg').startsWith('bg') ? 'bg-BG' : 'en-GB';
          const options = { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' };
          if (delivery.getUTCFullYear() !== parts.year) options.year = 'numeric';
          target.dateTime = iso;
          target.textContent = new Intl.DateTimeFormat(locale, options).format(delivery);
          target.dataset.date = iso;
        }
        const cutoffKey = cutoffDay.toISOString().slice(0, 10) + ':' + cutoffHour;
        if (this.cutoffKey !== cutoffKey) {
          this.cutoffTimestamp = zonedCutoff(cutoffDay, cutoffHour);
          this.cutoffKey = cutoffKey;
        }
        const remaining = Math.max(0, Math.floor((this.cutoffTimestamp - now) / 1000));
        const values = {
          hours: Math.floor(remaining / 3600),
          minutes: Math.floor(remaining / 60) % 60,
          seconds: remaining % 60
        };
        for (const [unit, value] of Object.entries(values)) {
          const element = this.querySelector('[data-fd-countdown-' + unit + ']');
          if (element) element.textContent = String(value).padStart(2, '0');
        }
        const countdown = this.querySelector('[data-fd-countdown]');
        const slot = this.querySelector('[data-fd-countdown-slot]');
        const showCountdown = this.cutoffTimestamp - now <= 10800000;
        if (countdown) countdown.hidden = !showCountdown;
        if (slot) slot.hidden = !showCountdown;
      } catch (_) {
        const countdown = this.querySelector('[data-fd-countdown]');
        if (countdown) countdown.hidden = true;
        const slot = this.querySelector('[data-fd-countdown-slot]');
        if (slot) slot.hidden = true;
      }
    }
  }
  customElements.define('fd-on-request-delivery', FdOnRequestDelivery);
})();
